/**
 * ★ Fase 91 (Doc 02) — Step progressivo OPZIONALE del form lead (prequalifica).
 * Dopo i 3 campi minimi (nome, email, telefono) l'utente può indicare, se vuole:
 *   • obiettivo (reddito / patrimonio / uso personale / misto)
 *   • budget indicativo in fasce (stesse bracket del backend /public/qualifica/config)
 *   • orizzonte decisionale (subito / 3 / 6 / oltre 6 mesi)
 * I valori viaggiano nel payload `prequal` di POST /api/public/funnel-lead e finiscono
 * nei campi qualificazione del CRM (senza sovrascrivere il lavoro del setter).
 * Nessun campo è obbligatorio: il form resta inviabile con i soli campi minimi.
 */
import React, { useState } from 'react';
import { ChevronDown, Target, Wallet, CalendarRange } from 'lucide-react';

export const PREQUAL_OPTIONS = {
  goal: [
    { id: 'reddito', it: 'Reddito / flussi da locazione', es: 'Renta / flujos por alquiler', en: 'Income / rental cash-flow' },
    { id: 'patrimonio', it: 'Diversificazione patrimoniale', es: 'Diversificación patrimonial', en: 'Wealth diversification' },
    { id: 'uso_personale', it: 'Uso personale / seconda casa', es: 'Uso personal / segunda vivienda', en: 'Personal use / second home' },
    { id: 'misto', it: 'Misto (reddito + uso personale)', es: 'Mixto (renta + uso personal)', en: 'Mixed (income + personal use)' },
  ],
  budget: [
    { id: 're_15_50', label: '€15.500 – €50.000' },
    { id: 're_50_100', label: '€50.000 – €100.000' },
    { id: 're_100_250', label: '€100.000 – €250.000' },
    { id: 're_250_500', label: '€250.000 – €500.000' },
    { id: 're_500_1m', label: '€500.000 – €1M' },
    { id: 're_1m_2_5m', label: '€1M – €2.500.000+' },
  ],
  timeline: [
    { id: 'subito', it: 'Subito (entro 30 giorni)', es: 'Ahora (en 30 días)', en: 'Now (within 30 days)' },
    { id: '3_months', it: 'Entro 3 mesi', es: 'En 3 meses', en: 'Within 3 months' },
    { id: '6_months', it: 'Entro 6 mesi', es: 'En 6 meses', en: 'Within 6 months' },
    { id: 'over_6_months', it: 'Oltre 6 mesi', es: 'Más de 6 meses', en: 'More than 6 months' },
  ],
};

const TXT = {
  it: {
    toggle: 'Vuoi una risposta più mirata? Aggiungi 3 dettagli (facoltativo)',
    toggleOpen: 'Nascondi dettagli facoltativi',
    goal: 'Obiettivo principale', budget: 'Budget indicativo', timeline: 'Orizzonte decisionale',
    choose: 'Seleziona…', note: 'Facoltativo · serve solo a prepararti una risposta pertinente.',
  },
  es: {
    toggle: '¿Quieres una respuesta más precisa? Añade 3 detalles (opcional)',
    toggleOpen: 'Ocultar detalles opcionales',
    goal: 'Objetivo principal', budget: 'Presupuesto orientativo', timeline: 'Horizonte de decisión',
    choose: 'Selecciona…', note: 'Opcional · solo sirve para prepararte una respuesta pertinente.',
  },
  en: {
    toggle: 'Want a more precise answer? Add 3 details (optional)',
    toggleOpen: 'Hide optional details',
    goal: 'Main goal', budget: 'Indicative budget', timeline: 'Decision horizon',
    choose: 'Select…', note: 'Optional · only used to prepare a relevant answer.',
  },
};

function SelectField({ icon: Icon, label, value, onChange, options, lang, theme, testId, placeholder }) {
  const dark = theme === 'dark';
  const style = dark
    ? { background: 'rgba(244,238,225,0.07)', border: '1px solid rgba(214,178,94,0.30)', color: value ? '#F4EEE1' : 'rgba(207,198,178,0.7)' }
    : { background: '#FFFFFF', border: '1px solid #DDD6C4', color: value ? '#111A17' : '#7A8784' };
  return (
    <label className="block">
      <span className={`mb-1 flex items-center gap-1.5 text-[11px] font-sans font-semibold tracking-[0.12em] uppercase ${dark ? '' : ''}`} style={{ color: dark ? '#D6B25E' : '#5F6B49' }}>
        <Icon className="w-3.5 h-3.5" aria-hidden="true" /> {label}
      </span>
      <div className="relative">
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full h-11 rounded-xl pl-4 pr-10 text-sm font-sans appearance-none focus:outline-none focus-visible:ring-2"
          style={{ ...style, caretColor: '#D6B25E' }}
          data-testid={testId}
          aria-label={label}
        >
          <option value="">{placeholder}</option>
          {options.map((o) => (
            <option key={o.id} value={o.id} style={{ color: '#111A17' }}>{o.label || o[lang] || o.it}</option>
          ))}
        </select>
        <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none" style={{ color: dark ? '#D6B25E' : '#5F6B49' }} aria-hidden="true" />
      </div>
    </label>
  );
}

/**
 * @param {object} props
 * @param {{goal?:string,budget_bracket?:string,timeline?:string}} props.value
 * @param {(v:object)=>void} props.onChange
 * @param {'it'|'es'|'en'} [props.lang]
 * @param {'dark'|'light'} [props.theme]
 * @param {string} [props.testIdPrefix]
 * @param {boolean} [props.defaultOpen]
 */
export function PrequalFields({ value = {}, onChange, lang = 'it', theme = 'dark', testIdPrefix = 'prequal', defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  const t = TXT[lang] || TXT.it;
  const dark = theme === 'dark';
  const set = (k) => (v) => onChange({ ...value, [k]: v || undefined });
  return (
    <div className="space-y-3" data-testid={`${testIdPrefix}-block`}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="w-full flex items-center justify-between gap-3 rounded-xl px-4 py-2.5 text-left text-[12px] sm:text-[13px] font-sans font-medium transition-colors duration-150 focus:outline-none focus-visible:ring-2"
        style={{
          background: dark ? 'rgba(214,178,94,0.08)' : 'rgba(95,107,73,0.08)',
          color: dark ? '#F4EEE1' : '#111A17',
          border: `1px dashed ${dark ? 'rgba(214,178,94,0.45)' : 'rgba(95,107,73,0.45)'}`,
        }}
        data-testid={`${testIdPrefix}-toggle`}
      >
        <span>{open ? t.toggleOpen : t.toggle}</span>
        <ChevronDown className={`w-4 h-4 shrink-0 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} style={{ color: dark ? '#D6B25E' : '#5F6B49' }} aria-hidden="true" />
      </button>
      {open && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid={`${testIdPrefix}-fields`}>
          <SelectField icon={Target} label={t.goal} value={value.goal || ''} onChange={set('goal')} options={PREQUAL_OPTIONS.goal} lang={lang} theme={theme} testId={`${testIdPrefix}-goal`} placeholder={t.choose} />
          <SelectField icon={Wallet} label={t.budget} value={value.budget_bracket || ''} onChange={set('budget_bracket')} options={PREQUAL_OPTIONS.budget} lang={lang} theme={theme} testId={`${testIdPrefix}-budget`} placeholder={t.choose} />
          <SelectField icon={CalendarRange} label={t.timeline} value={value.timeline || ''} onChange={set('timeline')} options={PREQUAL_OPTIONS.timeline} lang={lang} theme={theme} testId={`${testIdPrefix}-timeline`} placeholder={t.choose} />
          <p className="sm:col-span-3 text-[11px] font-sans" style={{ color: dark ? '#CFC6B2' : '#5A6B67' }}>{t.note}</p>
        </div>
      )}
    </div>
  );
}

/** Pulisce il valore prima dell'invio (solo chiavi valorizzate). */
export function cleanPrequal(value) {
  if (!value) return null;
  const out = {};
  ['goal', 'budget_bracket', 'timeline'].forEach((k) => { if (value[k]) out[k] = value[k]; });
  return Object.keys(out).length ? out : null;
}
